import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState, useCallback } from "react";
import { useApp } from "../store";
import { buildBookDocument } from "../lib/buildBookDocument";
import { renderInIframe, printIframe, extractBookmarks } from "../lib/pagedjsPreview";
import { applyBlockAction } from "../lib/clickToEditActions";
const BUILD_ID = Math.random().toString(36).slice(2, 8) + "_" + new Date().toISOString().slice(11, 19);
/**
 * Mapping Spread-Index → sichtbare Page-Indizes im Buchsatz.
 * Spread 0  = nur Seite 1  (Index 0, recto alleine)
 * Spread 1  = Seiten 2-3   (Indizes 1, 2)
 * Spread 2  = Seiten 4-5   (Indizes 3, 4)
 * Spread n  = Seiten 2n, 2n+1 (Indizes 2n-1, 2n) für n ≥ 1
 */
function isPageVisibleInSpread(pageIdx, spreadIdx, layout) {
    if (layout !== "spread")
        return pageIdx === spreadIdx;
    if (spreadIdx === 0)
        return pageIdx === 0;
    const startPage = 2 * spreadIdx - 1;
    return pageIdx === startPage || pageIdx === startPage + 1;
}
/** Umgekehrt: zu welchem Spread gehört eine gegebene Page (im Spread-Layout)? */
function spreadIndexOfPage(pageIdx, layout) {
    if (layout !== "spread")
        return pageIdx;
    if (pageIdx === 0)
        return 0;
    return Math.floor((pageIdx + 1) / 2);
}
/** Anzahl Spreads für totalPages im aktuellen Layout. */
function totalSpreads(totalPages, layout) {
    if (totalPages <= 0)
        return 1;
    if (layout !== "spread")
        return totalPages;
    // Seite 1 alleine + (totalPages - 1) Seiten in 2er-Paaren
    return 1 + Math.ceil(Math.max(0, totalPages - 1) / 2);
}
export default function PreviewPane() {
    const settings = useApp((s) => s.settings);
    const bodyHtml = useApp((s) => s.bodyHtml);
    const headings = useApp((s) => s.headings);
    const imageOverrides = useApp((s) => s.imageOverrides);
    const rawMarkdown = useApp((s) => s.rawMarkdown);
    const setRawMarkdown = useApp((s) => s.setRawMarkdown);
    const iframeRef = useRef(null);
    const [rendering, setRendering] = useState(false);
    const [pageCount, setPageCount] = useState(null);
    const [error, setError] = useState(null);
    const [logs, setLogs] = useState([]);
    const [showLogs, setShowLogs] = useState(false);
    // Marker-Sichtbarkeit (Default: an — sonst sieht der User die manuellen Marker
    // nach dem Setzen nicht und versteht das Tool nicht). Persistiert in localStorage.
    const [showMarkers, setShowMarkers] = useState(() => localStorage.getItem("md2bp:show-markers") !== "0");
    // Click-to-Edit Floating-Menu (Position im PreviewPane-Koordinatensystem)
    const [clickMenu, setClickMenu] = useState(null);
    // Pillen-Action-Menu (Click auf bestehende Marker-Pille im Preview)
    const [pillMenu, setPillMenu] = useState(null);
    // Bild-Menu (Click auf ein Bild im Preview → Größe ändern)
    const [imageMenu, setImageMenu] = useState(null);
    // Nach einer Marker-Aktion: Offset des betroffenen Blocks im neuen MD.
    // Wird beim nächsten Render verwendet, um dorthin zu scrollen statt zum Kapitelanfang.
    const scrollAnchorRef = useRef(null);
    // Preview-Ansichts-State (NICHT in settings — ist eine Viewer-Präferenz, kein Buch-Setting)
    const [layout, setLayout] = useState("spread");
    const [nav, setNav] = useState("scroll");
    const [zoom, setZoom] = useState(60); // Default 60 % damit Pages mit Background sichtbar
    const [currentIndex, setCurrentIndex] = useState(0);
    // Wenn Print-Spread-Setting aus, force Preview auf Einzelseite
    useEffect(() => {
        if (!settings.page.spreadLayout && layout === "spread")
            setLayout("single");
    }, [settings.page.spreadLayout, layout]);
    // Body-Klasse für Marker-Sichtbarkeit auf das aktuelle Iframe anwenden.
    // Muss nach jedem Re-Render erneut angewendet werden, weil Paged.js den Body
    // austauscht — das passiert weiter unten im Render-Effekt.
    const applyShowMarkersToIframe = useCallback(() => {
        const body = iframeRef.current?.contentDocument?.body;
        if (!body)
            return;
        body.classList.toggle("md2bp-show-markers", showMarkers);
    }, [showMarkers]);
    useEffect(() => {
        localStorage.setItem("md2bp:show-markers", showMarkers ? "1" : "0");
        applyShowMarkersToIframe();
    }, [showMarkers, applyShowMarkersToIframe]);
    // ID des aktuell sichtbaren H1 — wird beim Re-Render wiederhergestellt
    const currentChapterId = useRef(null);
    // HMR-Hygiene
    useEffect(() => {
        const el = iframeRef.current;
        if (!el)
            return;
        el.removeAttribute("sandbox");
        el.removeAttribute("srcdoc");
        console.log("[md2bp] PreviewPane mount — BUILD_ID=" + BUILD_ID);
    }, []);
    // Refs auf aktuelle View-State-Werte, damit die Funktion sich nicht
    // dauernd neu erstellt und Re-Renders triggert.
    const viewStateRef = useRef({ layout, nav, zoom, currentIndex });
    viewStateRef.current = { layout, nav, zoom, currentIndex };
    // Iframe-Style + Page-Visibility neu setzen — stabile Identität via useCallback ohne Deps
    const applyViewMode = useCallback(() => {
        const { layout, nav, zoom, currentIndex } = viewStateRef.current;
        const doc = iframeRef.current?.contentDocument;
        if (!doc)
            return;
        const pages = Array.from(doc.querySelectorAll(".pagedjs_page"));
        if (pages.length === 0)
            return;
        const pagesContainer = doc.querySelector(".pagedjs_pages");
        if (pagesContainer) {
            pagesContainer.dataset.layout = layout;
            pagesContainer.dataset.nav = nav;
            const s = zoom / 100;
            // setProperty mit "important" — sonst setzt Paged.js' eigenes Inline-Styling
            // (display: block) unsere Werte zurück.
            pagesContainer.style.setProperty("transform", s === 1 ? "none" : `scale(${s})`, "important");
            pagesContainer.style.setProperty("transform-origin", "top center", "important");
            pagesContainer.style.setProperty("padding", "14mm 0", "important");
            if (layout === "spread") {
                pagesContainer.style.setProperty("display", "grid", "important");
                pagesContainer.style.setProperty("grid-template-columns", "1fr 1fr", "important");
                pagesContainer.style.setProperty("column-gap", "0", "important");
                pagesContainer.style.setProperty("row-gap", "14mm", "important");
                pagesContainer.style.setProperty("flex-direction", "", "important");
                pagesContainer.style.setProperty("align-items", "start", "important");
            }
            else {
                pagesContainer.style.setProperty("display", "flex", "important");
                pagesContainer.style.setProperty("flex-direction", "column", "important");
                pagesContainer.style.setProperty("align-items", "center", "important");
                pagesContainer.style.setProperty("grid-template-columns", "", "important");
                pagesContainer.style.setProperty("row-gap", "14mm", "important");
            }
            if (doc.body)
                doc.body.style.setProperty("min-height", "100%", "important");
        }
        // Pages zuerst alle zurücksetzen (display + grid-column-start)
        pages.forEach((p) => {
            p.style.removeProperty("display");
            p.style.removeProperty("grid-column-start");
        });
        // Im Spread-Layout soll Seite 1 (recto) alleine rechts stehen, damit die
        // Doppelseiten danach als 2/3, 4/5, … erscheinen (klassischer Buchsatz).
        if (layout === "spread" && pages[0]) {
            pages[0].style.setProperty("grid-column-start", "2", "important");
        }
        if (nav === "paged") {
            pages.forEach((p, i) => {
                const visible = isPageVisibleInSpread(i, currentIndex, layout);
                if (!visible)
                    p.style.setProperty("display", "none", "important");
            });
        }
    }, []); // KEINE deps — Funktion liest aus viewStateRef
    // Render-Pipeline. Bewahrt das aktuelle Kapitel (per H1-ID) über Re-Renders hinweg.
    useEffect(() => {
        if (!iframeRef.current || !bodyHtml)
            return;
        // Sofort beim Settings-Change das aktuell sichtbare Kapitel festhalten
        trackCurrentChapter();
        const handle = setTimeout(async () => {
            const html = buildBookDocument({ settings, bodyHtml, headings, imageOverrides });
            fetch("/__debug?kind=html", { method: "POST", headers: { "Content-Type": "text/plain" }, body: html }).catch(() => { });
            setRendering(true);
            setError(null);
            setPageCount(null);
            const result = await renderInIframe(iframeRef.current, html);
            setLogs(result.log);
            setPageCount(result.pageCount);
            setError(result.error ?? null);
            setRendering(false);
            // Zur richtigen Stelle springen.
            // - Wenn nach einer Marker-Aktion ein scrollAnchor gesetzt ist: dort
            //   exakt verbleiben (data-md-start-Lookup im neuen DOM).
            // - Sonst Standard-Logik: Kapitel beibehalten.
            setTimeout(() => {
                applyViewMode();
                if (scrollAnchorRef.current !== null) {
                    if (!restoreScrollAnchor(scrollAnchorRef.current)) {
                        restoreChapterPosition();
                    }
                    scrollAnchorRef.current = null;
                }
                else {
                    restoreChapterPosition();
                }
                attachScrollTracker();
                attachClickToEdit();
                applyShowMarkersToIframe();
            }, 80);
            fetch("/__debug?kind=logs", {
                method: "POST", headers: { "Content-Type": "text/plain" },
                body: `BUILD_ID=${BUILD_ID}\npages=${result.pageCount}\nerror=${result.error ?? "none"}\n\n` + result.log.join("\n"),
            }).catch(() => { });
        }, 400);
        return () => clearTimeout(handle);
        // applyViewMode aus den Deps RAUS — die Funktion ist jetzt stable (keine inner deps)
        // und ein Re-Render des Iframes bei jedem Zoom/Layout-Wechsel ist nicht erwünscht
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings, bodyHtml, headings, imageOverrides]);
    /** Findet das aktuell sichtbare H1 und merkt sich seine ID. */
    function trackCurrentChapter() {
        const doc = iframeRef.current?.contentDocument;
        const win = iframeRef.current?.contentWindow;
        if (!doc || !win)
            return;
        const h1s = Array.from(doc.querySelectorAll("h1[id]"));
        if (h1s.length === 0)
            return;
        const { nav, layout, currentIndex } = viewStateRef.current;
        // Paged-mode: nimm das letzte H1, dessen Page-Index zum aktuellen Spread gehört
        if (nav === "paged") {
            const pages = Array.from(doc.querySelectorAll(".pagedjs_page"));
            let last = null;
            for (const h of h1s) {
                const page = h.closest(".pagedjs_page");
                if (!page)
                    continue;
                const pageIdx = pages.indexOf(page);
                if (pageIdx >= 0 && isPageVisibleInSpread(pageIdx, currentIndex, layout)) {
                    last = h;
                }
                else if (pageIdx > 2 * currentIndex) {
                    break;
                }
            }
            if (last?.id)
                currentChapterId.current = last.id;
            else if (h1s[0]?.id)
                currentChapterId.current = h1s[0].id;
            return;
        }
        // Scroll-mode: letztes H1, dessen Viewport-Top ≤ 100px (also schon passiert/grad sichtbar)
        let best = null;
        for (const h of h1s) {
            const top = h.getBoundingClientRect().top;
            if (top <= 100)
                best = h;
            else
                break;
        }
        if (best?.id)
            currentChapterId.current = best.id;
        else if (h1s[0]?.id)
            currentChapterId.current = h1s[0].id;
    }
    /**
     * Scrollt nach einer Marker-Aktion exakt zum betroffenen Block — anhand des
     * data-md-start-Offsets im neuen DOM. Liefert false, wenn der Block nicht
     * gefunden wurde (dann fällt der Caller auf Kapitel-Restore zurück).
     * Sucht den nächstgrößeren Offset, falls die exakte Position keinen Treffer
     * hat (z.B. wenn der Block zwischen Heading und nächstem Block liegt).
     */
    function restoreScrollAnchor(anchorOffset) {
        const doc = iframeRef.current?.contentDocument;
        if (!doc)
            return false;
        let target = doc.querySelector(`[data-md-start="${anchorOffset}"]`);
        if (!target) {
            // Nächstgelegenen Block finden (≥ anchor)
            const all = Array.from(doc.querySelectorAll("[data-md-start]"));
            let best = null;
            let bestDelta = Infinity;
            for (const el of all) {
                const s = parseInt(el.getAttribute("data-md-start") || "-1", 10);
                if (!Number.isFinite(s) || s < 0)
                    continue;
                const d = Math.abs(s - anchorOffset);
                if (d < bestDelta) {
                    bestDelta = d;
                    best = el;
                }
            }
            target = best;
        }
        if (!target)
            return false;
        const page = target.closest(".pagedjs_page");
        const { nav, layout } = viewStateRef.current;
        if (nav === "paged" && page) {
            const pages = Array.from(doc.querySelectorAll(".pagedjs_page"));
            const pageIdx = pages.indexOf(page);
            if (pageIdx >= 0) {
                setCurrentIndex(spreadIndexOfPage(pageIdx, layout));
            }
        }
        else {
            target.scrollIntoView({ behavior: "auto", block: "center" });
        }
        return true;
    }
    /** Stellt nach Re-Render die Position auf das gemerkte Kapitel wieder her. */
    function restoreChapterPosition() {
        const doc = iframeRef.current?.contentDocument;
        if (!doc || !currentChapterId.current)
            return;
        const target = doc.getElementById(currentChapterId.current);
        if (!target)
            return;
        const page = target.closest(".pagedjs_page");
        if (!page)
            return;
        const { nav, layout } = viewStateRef.current;
        if (nav === "paged") {
            const pages = Array.from(doc.querySelectorAll(".pagedjs_page"));
            const pageIdx = pages.indexOf(page);
            if (pageIdx >= 0) {
                setCurrentIndex(spreadIndexOfPage(pageIdx, layout));
            }
        }
        else {
            page.scrollIntoView({ behavior: "auto", block: "start" });
        }
    }
    /** Bindet Scroll-/Click-Listener im Iframe, um aktuelles Kapitel zu tracken. */
    function attachScrollTracker() {
        const win = iframeRef.current?.contentWindow;
        if (!win)
            return;
        let scheduled = false;
        const onScroll = () => {
            if (scheduled)
                return;
            scheduled = true;
            requestAnimationFrame(() => { scheduled = false; trackCurrentChapter(); });
        };
        win.removeEventListener("scroll", onScroll);
        win.addEventListener("scroll", onScroll, { passive: true });
        // Initial einmal tracken
        trackCurrentChapter();
    }
    /**
     * Click-Handler im Iframe-Document: findet den nächsten Block mit
     * data-md-start und öffnet das Floating-Menu an der Cursor-Position.
     * Re-installation bei jedem Re-Render notwendig, weil Paged.js den
     * Body komplett austauscht.
     */
    function attachClickToEdit() {
        const doc = iframeRef.current?.contentDocument;
        const frameRect = iframeRef.current?.getBoundingClientRect();
        if (!doc || !frameRect)
            return;
        const onClick = (e) => {
            const ev = e;
            const tgt = ev.target;
            if (!tgt)
                return;
            // Floating-Menü-Position relativ zum .preview-Container berechnen
            const previewEl = iframeRef.current?.closest(".preview");
            const prRect = previewEl?.getBoundingClientRect();
            const x = (prRect ? frameRect.left - prRect.left : 0) + ev.clientX + 8;
            const y = (prRect ? frameRect.top - prRect.top : 0) + ev.clientY + 8;
            // (1) Klick auf eine Marker-Pille → Pillen-Menu (Funktion zeigen + Entfernen)
            const pill = tgt.closest(".md2bp-marker-pill");
            if (pill) {
                const wrapper = pill.closest(".md2bp-pagebreak, .md2bp-keep, .md2bp-fullpage");
                if (!wrapper)
                    return;
                const start = parseInt(wrapper.getAttribute("data-md-start") || "-1", 10);
                const end = parseInt(wrapper.getAttribute("data-md-end") || "-1", 10);
                const kind = pill.getAttribute("data-marker-kind") || "";
                const subtype = pill.getAttribute("data-marker-subtype") || "";
                setClickMenu(null);
                setImageMenu(null);
                setPillMenu({ x, y, blockStart: start, blockEnd: end, kind, subtype });
                ev.stopPropagation();
                ev.preventDefault();
                return;
            }
            // (2) Klick auf ein Bild → Bild-Menu (Größe ändern)
            const img = tgt.closest("img[data-img-id]");
            if (img) {
                const mdEnd = parseInt(img.getAttribute("data-md-end") || "-1", 10);
                if (Number.isFinite(mdEnd) && mdEnd >= 0) {
                    setClickMenu(null);
                    setPillMenu(null);
                    setImageMenu({ x, y, mdEnd, curWidth: img.style.maxWidth || "" });
                    ev.stopPropagation();
                    ev.preventDefault();
                    return;
                }
            }
            // (3) Normaler Block-Klick → Block-Menu (Marker setzen)
            const block = tgt.closest("[data-md-start]");
            if (!block) {
                setClickMenu(null);
                setPillMenu(null);
                setImageMenu(null);
                return;
            }
            const offStr = block.getAttribute("data-md-start");
            if (!offStr)
                return;
            const offset = parseInt(offStr, 10);
            if (!Number.isFinite(offset))
                return;
            const blockPreview = (block.textContent || "").trim().slice(0, 60);
            setPillMenu(null);
            setImageMenu(null);
            setClickMenu({
                x,
                y,
                offset,
                blockTag: block.tagName.toLowerCase(),
                blockPreview,
            });
            ev.stopPropagation();
        };
        // Bestehende Listener entfernen via wrapped function — Iframe-Doc kann
        // Listener nicht inspeezieren, also pflegen wir's über ein Window-Property
        const w = iframeRef.current?.contentWindow;
        if (w?.__md2bp_clickHandler) {
            doc.removeEventListener("click", w.__md2bp_clickHandler, true);
        }
        if (w)
            w.__md2bp_clickHandler = onClick;
        doc.addEventListener("click", onClick, true);
    }
    // Reapply view mode wenn Toolbar-State sich ändert (ohne Iframe neu zu rendern)
    useEffect(() => {
        applyViewMode();
    }, [layout, nav, zoom, currentIndex, applyViewMode]);
    useEffect(() => {
        window.__md2bp_print = () => {
            if (iframeRef.current) {
                printIframe(iframeRef.current, {
                    widthMm: settings.page.widthMm,
                    heightMm: settings.page.heightMm,
                });
            }
        };
        // Bookmark-Map abgreifen — wird vom Bookmark-Export-Flow vor dem Druck aufgerufen.
        window.__md2bp_extract_bookmarks = () => {
            if (!iframeRef.current)
                return [];
            return extractBookmarks(iframeRef.current);
        };
        return () => {
            delete window.__md2bp_print;
            delete window.__md2bp_extract_bookmarks;
        };
    }, [settings.page.widthMm, settings.page.heightMm]);
    // Anzahl der "Screens" je Nav-Modus
    const totalScreens = pageCount && pageCount > 0
        ? totalSpreads(pageCount, layout)
        : 1;
    const canPrev = currentIndex > 0;
    const canNext = currentIndex < totalScreens - 1;
    // Tastatur-Shortcuts im paged-Modus
    useEffect(() => {
        if (nav !== "paged")
            return;
        const onKey = (e) => {
            if (e.target?.matches?.("input, select, textarea"))
                return;
            if (e.key === "ArrowRight" || e.key === "PageDown") {
                e.preventDefault();
                setCurrentIndex((i) => Math.min(totalScreens - 1, i + 1));
            }
            else if (e.key === "ArrowLeft" || e.key === "PageUp") {
                e.preventDefault();
                setCurrentIndex((i) => Math.max(0, i - 1));
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [nav, totalScreens]);
    return (_jsxs("div", { className: "preview", children: [_jsxs("div", { className: "preview-toolbar", style: { flexWrap: "wrap", gap: 8 }, children: [_jsxs("span", { className: "seg", children: [_jsx("button", { className: layout === "single" ? "on" : "", onClick: () => setLayout("single"), children: "Einzelseite" }), _jsx("button", { className: layout === "spread" ? "on" : "", onClick: () => setLayout("spread"), children: "Doppelseite" })] }), _jsxs("span", { className: "seg", children: [_jsx("button", { className: nav === "scroll" ? "on" : "", onClick: () => setNav("scroll"), children: "Scrollen" }), _jsx("button", { className: nav === "paged" ? "on" : "", onClick: () => setNav("paged"), children: "Bl\u00E4ttern" })] }), _jsx("button", { className: `btn ${showMarkers ? "primary" : "ghost"}`, style: { padding: "4px 10px", fontSize: 12 }, onClick: () => setShowMarkers((v) => !v), title: showMarkers
                            ? "Marker-Hinweise im Preview ausblenden (so sieht das gedruckte Buch aus)"
                            : "Hinweise zu manuell gesetzten Markern im Preview einblenden", children: showMarkers ? "● Marker-Hinweise" : "○ Marker-Hinweise" }), nav === "paged" && (_jsxs("span", { style: { display: "inline-flex", alignItems: "center", gap: 4 }, children: [_jsx("button", { className: "btn", style: { padding: "4px 12px", fontSize: 13, minWidth: 36 }, disabled: !canPrev, onClick: () => setCurrentIndex((i) => Math.max(0, i - 1)), title: "Vorherige (\u2190)", children: "\u25C0" }), _jsxs("span", { style: { fontFamily: "var(--mono)", fontSize: 12, minWidth: 70, textAlign: "center" }, children: [currentIndex + 1, " / ", totalScreens] }), _jsx("button", { className: "btn", style: { padding: "4px 12px", fontSize: 13, minWidth: 36 }, disabled: !canNext, onClick: () => setCurrentIndex((i) => Math.min(totalScreens - 1, i + 1)), title: "N\u00E4chste (\u2192)", children: "\u25B6" })] })), _jsxs("span", { style: { display: "inline-flex", alignItems: "center", gap: 4, marginLeft: 4 }, children: [_jsx("button", { className: "btn ghost", style: { padding: "2px 10px", fontSize: 14 }, onClick: () => setZoom((z) => Math.max(30, z - 10)), title: "Kleiner", children: "\u2212" }), _jsxs("span", { style: { fontFamily: "var(--mono)", fontSize: 11, minWidth: 36, textAlign: "center" }, children: [zoom, "%"] }), _jsx("button", { className: "btn ghost", style: { padding: "2px 10px", fontSize: 14 }, onClick: () => setZoom((z) => Math.min(200, z + 10)), title: "Gr\u00F6\u00DFer", children: "+" }), _jsx("button", { className: "btn ghost", style: { padding: "2px 8px", fontSize: 11 }, onClick: () => setZoom(100), children: "100%" }), _jsx("button", { className: "btn ghost", style: { padding: "2px 8px", fontSize: 11 }, onClick: () => setZoom(60), children: "Fit" })] }), _jsx("span", { className: "grow" }), rendering ? (_jsx("span", { style: { color: "var(--ink-3)" }, children: "Rendere\u2026" })) : error ? (_jsxs("span", { style: { color: "#b85c2a" }, children: ["\u26A0 ", error.slice(0, 60), error.length > 60 ? "…" : ""] })) : pageCount != null ? (_jsxs("span", { style: { color: "var(--ink-3)", fontFamily: "var(--mono)", fontSize: 11 }, children: [pageCount, " Seiten"] })) : null, logs.length > 0 && (_jsx("button", { className: "btn ghost", style: { padding: "2px 8px", fontSize: 11 }, onClick: () => setShowLogs((v) => !v), children: showLogs ? "Logs aus" : `Logs (${logs.length})` }))] }), showLogs && (_jsx("div", { style: {
                    maxHeight: 240, overflow: "auto", padding: "8px 12px",
                    background: "#1f1f1d", color: "#cfcec8",
                    fontFamily: "var(--mono)", fontSize: 11, lineHeight: 1.45,
                    borderBottom: "1px solid var(--line)",
                    whiteSpace: "pre-wrap", wordBreak: "break-word",
                }, children: logs.map((l, i) => _jsx("div", { children: l }, i)) })), _jsx("iframe", { ref: iframeRef, className: "preview-iframe", title: "Buch-Vorschau" }), clickMenu && (_jsx(ClickMenu, { x: clickMenu.x, y: clickMenu.y, blockTag: clickMenu.blockTag, blockPreview: clickMenu.blockPreview, onClose: () => setClickMenu(null), onAction: (action) => {
                    const result = applyBlockAction(rawMarkdown, clickMenu.offset, action);
                    if (result.md !== rawMarkdown) {
                        scrollAnchorRef.current = result.newOffset;
                        setRawMarkdown(result.md);
                    }
                    setClickMenu(null);
                } })), imageMenu && (_jsx(ImageMenu, { x: imageMenu.x, y: imageMenu.y, curWidth: imageMenu.curWidth, onClose: () => setImageMenu(null), onAction: (action) => {
                    const result = applyBlockAction(rawMarkdown, imageMenu.mdEnd, action);
                    if (result.md !== rawMarkdown) {
                        scrollAnchorRef.current = result.newOffset;
                        setRawMarkdown(result.md);
                    }
                    setImageMenu(null);
                } })), pillMenu && (_jsx(PillMenu, { x: pillMenu.x, y: pillMenu.y, kind: pillMenu.kind, subtype: pillMenu.subtype, onClose: () => setPillMenu(null), onRemove: () => {
                    // Marker entfernen: applyBlockAction sucht rückwärts ab Offset.
                    // Wir nutzen blockEnd + 1, damit der Marker im Suchfenster liegt.
                    const result = applyBlockAction(rawMarkdown, pillMenu.blockEnd + 1, {
                        kind: "remove-marker-before",
                    });
                    if (result.md !== rawMarkdown) {
                        scrollAnchorRef.current = result.newOffset;
                        setRawMarkdown(result.md);
                    }
                    setPillMenu(null);
                } }))] }));
}
/**
 * Menü zum Ändern der Bildbreite. Schreibt eine Pandoc-Breitenangabe
 * (`{width=NN%}`) in die Markdown-Quelle — überlebt damit Reparse + Export.
 */
function ImageMenu({ x, y, curWidth, onClose, onAction, }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape")
                onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    // Aktuelle Prozent-Breite aus dem Inline-Style (z. B. "min(50%, 100%)") lesen.
    const curPct = (() => {
        const m = curWidth.match(/(\d+)\s*%/);
        return m ? parseInt(m[1], 10) : null;
    })();
    const presets = [100, 75, 66, 50, 40, 33, 25];
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "click-menu-backdrop", onClick: onClose }), _jsxs("div", { className: "click-menu image-menu", style: { left: x, top: y }, children: [_jsxs("div", { className: "click-menu-header", children: [_jsx("span", { className: "click-menu-tag", children: "Bild" }), _jsx("span", { className: "click-menu-preview", children: "Breite \u00E4ndern" })] }), _jsx("div", { className: "image-menu-grid", children: presets.map((p) => (_jsxs("button", { className: curPct === p ? "on" : "", onClick: () => onAction({ kind: "image-width", pct: p }), children: [p, "%"] }, p))) }), _jsx("div", { className: "click-menu-sep" }), _jsx("button", { onClick: () => onAction({ kind: "image-reset" }), children: "Auf Standardbreite zur\u00FCcksetzen" })] })] }));
}
function PillMenu({ x, y, kind, subtype, onClose, onRemove, }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape")
                onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    const labels = {
        pagebreak: "Seitenumbruch",
        keep: "Block zusammenhalten",
        fullpage: "Eigene Seite",
    };
    const subtypeLabels = {
        page: "egal welche Seite",
        recto: "→ rechte Seite (recto)",
        verso: "→ linke Seite (verso)",
        blank: "+ Leerseite einschieben",
    };
    const activeLabel = kind === "pagebreak"
        ? `${labels.pagebreak} (${subtypeLabels[subtype] || subtype || "page"})`
        : labels[kind] || kind;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "click-menu-backdrop", onClick: onClose }), _jsxs("div", { className: "click-menu pill-menu", style: { left: x, top: y }, children: [_jsxs("div", { className: "click-menu-header", children: [_jsx("div", { className: "pill-menu-title", children: "Aktive Marker-Funktion" }), _jsx("div", { className: "pill-menu-active", children: activeLabel })] }), _jsx("button", { className: "danger", onClick: onRemove, children: "Marker entfernen" })] })] }));
}
/**
 * Schwebendes Aktions-Menü für Click-to-Edit. Schließt sich beim Klick außerhalb
 * (Backdrop) oder Escape.
 */
function ClickMenu({ x, y, blockTag, blockPreview, onClose, onAction, }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape")
                onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "click-menu-backdrop", onClick: onClose }), _jsxs("div", { className: "click-menu", style: { left: x, top: y }, children: [_jsxs("div", { className: "click-menu-header", children: [_jsx("span", { className: "click-menu-tag", children: blockTag }), _jsxs("span", { className: "click-menu-preview", children: ["\u201E", blockPreview, blockPreview.length >= 60 ? "…" : "", "\""] })] }), _jsx("button", { onClick: () => onAction({ kind: "pagebreak", type: "page" }), children: "Seitenumbruch davor" }), _jsx("button", { onClick: () => onAction({ kind: "pagebreak", type: "recto" }), children: "Seitenumbruch davor \u2192 rechte Seite" }), _jsx("button", { onClick: () => onAction({ kind: "pagebreak", type: "verso" }), children: "Seitenumbruch davor \u2192 linke Seite" }), _jsx("button", { onClick: () => onAction({ kind: "pagebreak", type: "blank" }), children: "Leerseite davor einschieben" }), _jsx("div", { className: "click-menu-sep" }), _jsx("button", { onClick: () => onAction({ kind: "keep" }), children: "Block + Folgeabsatz zusammenhalten" }), _jsx("button", { onClick: () => onAction({ kind: "fullpage" }), children: "Block auf eigene Seite stellen" }), _jsx("div", { className: "click-menu-sep" }), _jsx("button", { className: "danger", onClick: () => onAction({ kind: "remove-marker-before" }), children: "Marker davor entfernen" })] })] }));
}
